import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { User, Briefcase, FolderOpen, Instagram } from 'lucide-react';
import CustomFont from '../assets/fonts/GRADIOO.ttf';
import LogoImage from '../assets/images/IMG_5352-removebg-preview.png';

const Navigation = () => {
  const [activeSection, setActiveSection] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);

  // Load animation trigger
  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Section detection with Intersection Observer
  useEffect(() => {
    const sections = document.querySelectorAll('#about, #services, #portfolio, #contact');
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.3) {
            setActiveSection(`#${entry.target.id}`);
          }
        });
      },
      {
        threshold: [0.3],
        rootMargin: '-80px 0px -50% 0px'
      }
    );

    sections.forEach((section) => observer.observe(section));
    
    return () => observer.disconnect();
  }, []);

  const navItems = useMemo(
    () => [
      { href: '#about', label: 'About', icon: User },
      { href: '#services', label: 'Services', icon: Briefcase },
      { href: '#portfolio', label: 'Portfolio', icon: FolderOpen },
    ],
    []
  );

  // Discord SVG Component
  const DiscordIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
    </svg>
  );

  const socialLinks = [
    { href: 'https://discord.com/users/339553678295826432', icon: DiscordIcon, label: 'Discord' },
    { href: 'https://www.instagram.com/adaminparallel', icon: Instagram, label: 'Instagram' },

  ];

  // Smooth scroll function
  const handleNavClick = useCallback((href) => {
    if (href === '#top') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }

    const element = document.querySelector(href);
    if (element) {
      const navHeight = 80;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      const targetPosition = elementPosition - navHeight;
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  }, []);

  return (
    <>
      {/* Font loading */}
      <style jsx>{`
        @font-face {
          font-family: 'CustomFont';
          src: url(${CustomFont}) format('truetype');
          font-weight: 400;
          font-style: normal;
          font-display: swap;
        }
      `}</style>

      {/* Desktop Sidebar - w-80 = 320px */}
      <nav className={`hidden lg:block fixed top-0 left-0 h-screen w-80 z-50 transition-all duration-500 ease-out ${
        isLoaded ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'
      }`}>
        <div className="glass-sidebar h-full relative">
          {/* Content */}
          <div className="relative z-10 p-8 h-full flex flex-col">
            
            {/* Header with Apple-style dots */}
            <div className="flex items-center justify-between mb-8 pt-4">
              <div className="flex space-x-2">
                <div className="w-3 h-3 bg-gradient-to-br from-red-400 to-red-600 rounded-full animate-pulse"></div>
                <div className="w-3 h-3 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-3 h-3 bg-gradient-to-br from-green-400 to-green-600 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
              </div>
              <h1 className="text-cotton font-semibold text-lg tracking-wide drop-shadow-lg" style={{ fontFamily: 'CustomFont, Georgia, serif' }}>
                Portfolio
              </h1>
            </div>
            
            {/* Profile Section */}
            <div
              className={`flex items-center space-x-4 mb-12 cursor-pointer group transition-all duration-300 ${
                isLoaded ? 'animate-fade-in' : ''
              }`}
              onClick={() => handleNavClick('#top')}
              style={{ animationDelay: '200ms' }}
            >
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-cherry-red via-maroon to-noir-black rounded-2xl flex items-center justify-center group-hover:scale-105 transition-all duration-300 overflow-hidden shadow-2xl">
                  <img
                    src={LogoImage}
                    alt="Adam Harmane Logo"
                    className="w-full h-full object-cover relative z-10"
                    loading="eager"
                  />
                </div>
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-green-400 to-green-600 rounded-full border-2 border-cotton animate-pulse"></div>
              </div>
              <div>
                <h2 className="text-cotton font-semibold text-xl" style={{ fontFamily: 'CustomFont, Georgia, serif' }}>
                  Adam Harmane
                </h2>
                <p className="text-cotton/70 text-sm" style={{ fontFamily: 'CustomFont, Georgia, serif' }}>
                  Full Stack Developer
                </p>
                <p className="text-blue-400 text-xs font-medium flex items-center space-x-1" style={{ fontFamily: 'CustomFont, Georgia, serif' }}>
                  <span>Founder & Owner •</span>
                  <span> Webfolio.ma</span>
                </p>
              </div>
            </div>

            {/* Navigation Items */}
            <div className="space-y-2 flex-1">
              {navItems.map((item, index) => {
                const IconComponent = item.icon;
                const isActive = activeSection === item.href;
                
                return (
                  <div 
                    key={item.href}
                    className={`${
                      isLoaded ? 'animate-fade-in' : ''
                    }`}
                    style={{ animationDelay: `${(index + 3) * 100}ms` }}
                  >
                    <a
                      href={item.href}
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavClick(item.href);
                      }}
                      className={`flex items-center space-x-3 px-4 py-3 transition-all duration-300 group relative ${
                        isActive 
                          ? 'text-cherry-red' 
                          : 'text-cotton/70 hover:text-cotton'
                      }`}
                    >
                      <div className={`transition-all duration-300 ${
                        isActive ? 'text-cherry-red' : 'text-cotton/70 group-hover:text-cotton'
                      }`}>
                        <IconComponent className="w-5 h-5" />
                      </div>
                      
                      <span className="font-medium" style={{ fontFamily: 'CustomFont, Georgia, serif' }}>
                        {item.label}
                      </span>
                      
                      {/* Active indicator */}
                      {isActive && (
                        <div className="absolute right-3 w-2 h-2 bg-cherry-red rounded-full animate-pulse"></div>
                      )}
                    </a>
                  </div>
                );
              })}
            </div>

            {/* Social Links Section */}
            <div className="mb-6">
              <h3 className="text-cotton/50 text-xs font-semibold uppercase tracking-widest mb-4" style={{ fontFamily: 'CustomFont, Georgia, serif' }}>
                CONNECT
              </h3>
              <div className="flex space-x-4 justify-center">
                {socialLinks.map((social, index) => {
                  const IconComponent = social.icon;
                  return (
                    <a 
                      key={social.label}
                      href={social.href} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={`text-cotton/60 hover:text-cotton transition-all duration-300 hover:scale-110 ${
                        isLoaded ? 'animate-fade-in' : ''
                      }`}
                      style={{ animationDelay: `${700 + index * 100}ms` }}
                    >
                      <IconComponent className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Bottom Section */}
            <div className={`mt-auto pt-6 ${isLoaded ? 'animate-fade-in' : ''}`} style={{ animationDelay: '1200ms' }}>
              <div className="text-cotton/40 text-sm text-center" style={{ fontFamily: 'CustomFont, Georgia, serif' }}>
                © 2026 Adam Harmane
              </div>
            </div>
          </div>
        </div>
      </nav>

      <style jsx>{`
        /* Color Variables */
        :root {
          --cotton: #EDEBDD;
          --cherry-red: #810100;
          --maroon: #630000;
          --noir-black: #1B1717;
        }

        .text-cotton { color: var(--cotton); }
        .text-cherry-red { color: var(--cherry-red); }
        .text-maroon { color: var(--maroon); }
        .text-noir-black { color: var(--noir-black); }
        .bg-cotton { background-color: var(--cotton); }
        .bg-cherry-red { background-color: var(--cherry-red); }
        .bg-maroon { background-color: var(--maroon); }
        .bg-noir-black { background-color: var(--noir-black); }

        .from-cotton { --tw-gradient-from: var(--cotton); }
        .to-cotton { --tw-gradient-to: var(--cotton); }
        .from-cherry-red { --tw-gradient-from: var(--cherry-red); }
        .to-cherry-red { --tw-gradient-to: var(--cherry-red); }
        .from-maroon { --tw-gradient-from: var(--maroon); }
        .to-maroon { --tw-gradient-to: var(--maroon); }
        .from-noir-black { --tw-gradient-from: var(--noir-black); }
        .to-noir-black { --tw-gradient-to: var(--noir-black); }

        /* Clean Glassmorphism Effects */
        .glass-sidebar {
          background: rgba(27, 23, 23, 0.8);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-right: 1px solid rgba(237, 235, 221, 0.1);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
        }

        /* Smooth Animations */
        @keyframes fade-in {
          from { 
            opacity: 0; 
            transform: translateY(20px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }

        .animate-fade-in { 
          animation: fade-in 0.6s ease-out forwards; 
          opacity: 0;
        }
        
        /* Smooth scrolling */
        html {
          scroll-behavior: smooth;
        }

        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 4px;
        }
        
        ::-webkit-scrollbar-track {
          background: transparent;
        }
        
        ::-webkit-scrollbar-thumb {
          background: rgba(237, 235, 221, 0.2);
          border-radius: 2px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(237, 235, 221, 0.3);
        }

        /* Focus styles for accessibility */
        a:focus {
          outline: 2px solid rgba(129, 1, 0, 0.5);
          outline-offset: 2px;
          border-radius: 8px;
        }

        /* Pulse animation for active indicators */
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.1);
          }
        }

        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </>
  );
};

export default Navigation;